import { IColumnPreferences } from './columnPreferences.types';

/**
 * Removes locked columns from a list, preserving relative order of the rest.
 * Locked columns (e.g. Title, Author, Tags) are never draggable or hideable.
 */
export const getArrangeableColumns = (allColumns: string[], lockedColumns: string[]): string[] =>
  allColumns.filter(column => !lockedColumns.includes(column));

/**
 * Reinserts locked columns back into their original index positions
 * around a (possibly reordered) list of arrangeable columns, so locked
 * columns never move even though everything else can.
 */
export const mergeWithLockedColumns = (
  originalColumns: string[],
  lockedColumns: string[],
  arrangedColumns: string[]
): string[] => {
  const queue = [...arrangedColumns];

  // Locked columns stay in their original slot. Every other slot is filled,
  // in order, from the arranged queue. If the queue runs out (e.g. the
  // original list contains columns that aren't part of the arranged set,
  // such as transient columns), keep the original name rather than emitting
  // `undefined` - callers can safely filter/dedupe afterwards.
  return originalColumns.map(column => {
    if (lockedColumns.includes(column) || queue.length === 0) {
      return column;
    }

    return queue.shift() as string;
  });
};

/**
 * antd freezes columns by `fixed: 'left' | 'right'`, and they must stay
 * contiguous at their edge. The table has a fixed number of left/right pinned
 * "slots" (however many columns were originally fixed). We keep the user's
 * chosen order untouched and instead re-derive `fixed` by position: the first
 * N columns become the left-pinned slots and the last M the right-pinned slots.
 *
 * This means a scrollable column dragged into a pinned slot *becomes* pinned
 * there (staying put on horizontal scroll, no blank gap), and a pinned column
 * dragged out of the pinned region becomes scrollable - while the pinned count
 * stays stable. Columns whose `fixed` is unchanged keep their identity; only
 * the ones that flip are shallow-cloned.
 */
export const normalizeFixedColumns = <T extends { fixed?: string }>(columns: T[]): T[] => {
  const leftCount = columns.filter(column => column.fixed === 'left').length;
  const rightCount = columns.filter(column => column.fixed === 'right').length;
  const rightStart = columns.length - rightCount;

  return columns.map((column, index) => {
    let fixed = '';

    if (index < leftCount) {
      fixed = 'left';
    } else if (index >= rightStart) {
      fixed = 'right';
    }

    return (column.fixed || '') === fixed ? column : { ...column, fixed };
  });
};

/**
 * Moves an item within an array from one index to another, returning a new array.
 */
export const reorder = <T, >(list: T[], startIndex: number, endIndex: number): T[] => {
  if (startIndex === -1 || endIndex === -1) {
    return list; }

  const result = [...list];
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export const buildDefaultPreferences = (
  allColumns: string[],
  lockedColumns: string[]
): IColumnPreferences => ({
  order: getArrangeableColumns(allColumns, lockedColumns),
  hidden: []
});

/**
 * Builds column preferences from an explicit allow-list of columns that should
 * be visible (e.g. a role-based default). Every arrangeable column not present
 * in `visibleColumns` is hidden; locked columns are always visible and are
 * ignored here. Columns in `visibleColumns` that don't exist in the current
 * schema are simply skipped. Returns `null` when no allow-list is supplied, so
 * callers can fall back to the standard "show everything" default.
 */
export const buildVisibleColumnPreferences = (
  visibleColumns: string[] | undefined,
  allColumns: string[],
  lockedColumns: string[]
): IColumnPreferences | null => {
  if (!visibleColumns?.length) {
    return null; }

  const arrangeable = getArrangeableColumns(allColumns, lockedColumns);
  const visibleSet = new Set(visibleColumns);

  return {
    order: arrangeable,
    hidden: arrangeable.filter(column => !visibleSet.has(column))
  };
};

export const getShowAllState = (
  arrangeableColumns: string[],
  hidden: string[]
): 'all' | 'none' | 'partial' => {
  if (hidden.length === 0) {
    return 'all'; }

  if (hidden.length >= arrangeableColumns.length) {
    return 'none'; }

  return 'partial';
};

export const filterColumnsBySearch = (columns: string[], search: string): string[] => {
  if (!search.trim()) {
    return columns; }

  const term = search.trim().toLowerCase();

  return columns.filter(column => column.toLowerCase().includes(term));
};

export const reconcilePreferences = (
  allColumns: string[],
  lockedColumns: string[],
  saved: IColumnPreferences | null
): IColumnPreferences => {
  const arrangeable = getArrangeableColumns(allColumns, lockedColumns);

  if (!saved) {
    return { order: arrangeable, hidden: [] }; }

  const validOrder = saved.order.filter(c => arrangeable.includes(c));
  const newColumns = arrangeable.filter(c => !validOrder.includes(c));

  return {
    order: [...validOrder, ...newColumns],
    hidden: saved.hidden.filter(c => arrangeable.includes(c))
  };
};